"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateService = void 0;
const moment = require("moment");
class DateService {
    static REGEX_DATE_STRING = /^\d{4}-\d{2}-\d{2}T([a-zA-Z0-9_ :.'"-]+)Z$/;
    static REGEX_UTC_DATE_STRING = /^([A-Z][a-z]{2}), (\d{2}) ([A-Z][a-z]{2}) (\d{4}) (\d{2}):(\d{2}):(\d{2}) GMT$/;
    static REGEX_DATE_WITH_OFFSET_STRING = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?(?:(\+\d{2}(?:[-:])?\d{2})|Z)$/;
    constructor() { }
    /**
     * Converts a value to a date, accepts the following valid argument types:
     *  - a string formatted as 2020-12-05T18:38:24.145Z
     *  - an instance of firestore.Timestamp
     *  - an json version of Timestamp {_nanoseconds: 473000000,_seconds: 1607480898}
     *  - a number that represents a time offset, the result of date.getTime()
     *
     * @param {} input
     * @param {} ignoreNumbers -- Optional: ignore number types default is false
     *
     * @warning This method returns with no effect if the passed in value does not meet any of the criteria for conversion
     *
     * @returns The converted date or THE VALUE PASSED IN if the paramter does not meet any of the valid argument types
     */
    static toDate(input, ignoreNumbers = false) {
        if (!input) {
            return input;
        }
        if (input instanceof Date) {
            return input;
        }
        if (DateService.isConvertibleString(input)) {
            return new Date(input);
        }
        if (DateService.isBigQueryTimestamp(input)) {
            return new Date(input.value);
        }
        if (!ignoreNumbers && DateService.isConvertibleNumber(input)) {
            return new Date(input);
        }
        if (DateService.isFirestoreTimestampObject(input)) {
            return DateService.convertFirestoreTimestamp(input);
        }
        return input;
    }
    static validateStringIsDate(date, expectedFormat) {
        return moment(date, expectedFormat, true).isValid();
    }
    static isConvertibleNumber(num) {
        if (typeof num === 'number' && Math.floor(num) === num) {
            return new Date(num);
        }
        return false;
    }
    static convertTimeToMs(duration) {
        const times = (num, num2) => num * (num2 ?? 0);
        const milliseconds = (milliseconds) => times(1, milliseconds);
        const seconds = (seconds) => milliseconds(times(1000, seconds));
        const minutes = (minutes) => seconds(times(60, minutes));
        const hours = (hours) => minutes(times(60, hours));
        const days = (days) => hours(times(24, days));
        const weeks = (weeks) => days(times(7, weeks));
        return (milliseconds(duration.milliseconds) +
            seconds(duration.seconds) +
            minutes(duration.minutes) +
            hours(duration.hours) +
            days(duration.days) +
            weeks(duration.weeks));
    }
    static isConvertibleString(str) {
        // Regex to match a date represented as a string, ex: 2020-12-05T18:38:24.145Z
        // 4 digits followed by a dash
        // 2 digits followed by a dash
        // 2 digits followed by a T
        // Any char or digit or : or . or - of any length
        // Ending with a Z
        if (typeof str !== 'string') {
            return false;
        }
        const regex = new RegExp(this.REGEX_DATE_STRING);
        const regexWithOffset = new RegExp(this.REGEX_DATE_WITH_OFFSET_STRING);
        if (!regex.test(str) && !regexWithOffset.test(str) && !this.REGEX_UTC_DATE_STRING.test(str)) {
            return false;
        }
        const converted = new Date(str);
        if (isNaN(converted.getTime())) {
            return false;
        }
        else {
            return true;
        }
    }
    /**
     * Converts a string representation of a date to a date object, the parameter must meet the following criteria:
     *  - it is a string type
     *  - it is formatted like 2020-12-05T18:38:24.145Z
     *  - it tests positive against the regex: /^\d{4}-\d{2}-\d{2}T([a-zA-Z0-9_ :.'"-]+)Z$/
     *
     * @param {*} str
     *
     * @returns A date object initialized with the string or undefined if conditions are not met
     */
    static convertString(str) {
        // Regex to match a date represented as a string, ex: 2020-12-05T18:38:24.145Z
        // 4 digits followed by a dash
        // 2 digits followed by a dash
        // 2 digits followed by a T
        // Any char or digit or : or . or - of any length
        // Ending with a Z
        const REGEX_DATE_STRING = /^\d{4}-\d{2}-\d{2}T([a-zA-Z0-9_ :.'"-]+)Z$/;
        if (typeof str !== 'string') {
            return undefined;
        }
        const regex = new RegExp(REGEX_DATE_STRING);
        if (!regex.test(str)) {
            return undefined;
        }
        const converted = new Date(str);
        if (isNaN(converted.getTime())) {
            return undefined;
        }
        else {
            return converted;
        }
    }
    /**
     * Tests the parameter to determine if it is a JSON version firestore.Timestamp, the value will have the following form if true:
     *  {_nanoseconds: 473000000,_seconds: 1607480898}
     * @param {*} obj
     *
     * @returns True if the paramter matches the accepted form, otherwise false
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static isFirestoreTimestampObject(obj) {
        const seconds = obj._seconds ?? obj.seconds;
        const nanoseconds = obj._nanoseconds ?? obj.nanoseconds;
        const secondsExists = seconds !== undefined && seconds !== null;
        const nanosecondsExists = nanoseconds !== undefined && nanoseconds !== null;
        return typeof obj === 'object' && secondsExists && nanosecondsExists;
    }
    /**
     * Converts the paramter to a date if it is a json version of a firestore.Timestamp
     *
     * @param {*} obj
     *
     * @returns The converted date or the original object if it is not a firestore.Timestamp
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static convertIfFirestoreTimestamp(obj) {
        if (DateService.isFirestoreTimestampObject(obj)) {
            return DateService.convertFirestoreTimestamp(obj);
        }
        else {
            return obj;
        }
    }
    static convertFirestoreTimestamp(obj) {
        const seconds = obj._seconds ?? obj.seconds;
        const nanoseconds = obj._nanoseconds ?? obj.nanoseconds;
        // convert seconds and nanoseconds to milliseconds. 1 second is 1k milliseconds. 1 millisecond is 1 million nanoseconds
        return new Date(seconds * 1000 + nanoseconds / 1000000);
    }
    static isGcpProtobufTimestamp(obj) {
        if (typeof obj !== 'object')
            return false;
        if (Object.keys(obj).length > 2)
            return false;
        const seconds = obj.seconds;
        const nanos = obj.nanos;
        return !isNil(seconds) && !isNil(nanos);
    }
    static convertIfGcpProtobufTimestamp(obj) {
        if (!DateService.isGcpProtobufTimestamp(obj))
            return obj;
        return DateService.convertIfFirestoreTimestamp({ seconds: obj.seconds, nanoseconds: obj.nanos });
    }
    /**
     * Recursively evaluates all values in an object and converts to a date if applicable
     * The following are applicable values that will be converted:
     *      - a string formatted as 2020-12-05T18:38:24.145Z
     *      - an instance of firestore.Timestamp
     *      - an json version of Timestamp {_nanoseconds: 473000000,_seconds: 1607480898}
     * @param {*} obj
     *
     * @returns A new instance of an object with converted dates where applicable
     */
    static convertAllDateFields(obj, makeTimestamp = false) {
        if (!obj || typeof obj !== 'object') {
            return obj;
        }
        if (Array.isArray(obj)) {
            return obj.map(item => DateService.convertAllDateFields(item, makeTimestamp));
        }
        const result = {};
        DateService.recurseObj(obj, result, makeTimestamp);
        return result;
    }
    static recurseObj(obj, result, makeTimestamp = false) {
        for (const key in obj) {
            const value = obj[key];
            if (!value) {
                result[key] = value;
                continue;
            }
            if (typeof value === 'object' && !this.isFirestoreTimestampObject(value) && !DateService.isBigQueryTimestamp(value)) {
                DateService.recurseObj(value, value, makeTimestamp);
            }
            const converted = DateService.toDate(value, true);
            if (converted && converted instanceof Date) {
                result[key] = makeTimestamp ? converted.getTime() : converted;
            }
            else {
                result[key] = value;
            }
        }
    }
    static isBigQueryTimestamp(date, key) {
        if (Object.getPrototypeOf(date) !== undefined && date.constructor.name === 'BigQueryTimestamp') {
            return true;
        }
        return typeof date.value === 'string' && Object.keys(date).length === 1 && DateService.isConvertibleString(date.value);
    }
    /**
     * Add to a date
     *
     * @param {*} date1 -- a javascript date
     * @param {*} valueToAdd -- number value to add to date
     * @param {*} units -- moment.js units ex 'days' || 'months'
     *
     * @returns A date
     */
    static add(date1, valueToAdd, units) {
        return moment(date1).add(valueToAdd, units).toDate();
    }
    static addDuration(date, duration) {
        const increment = (value, units, lastDate) => {
            return this.add(lastDate, value, units);
        };
        return this.operateOnDate(date, duration, increment);
    }
    static subtractDuration(date, duration) {
        const decrement = (value, units, lastDate) => {
            return this.subtract(lastDate, value, units);
        };
        return this.operateOnDate(date, duration, decrement);
    }
    /**
     * Checks if a duration1 is less than duration2
     *
     * @param {*} duration1
     * @param {*} duration2
     */
    static durationIsLessThan(duration1, duration2) {
        const compareDate = new Date();
        const firstDate = DateService.addDuration(compareDate, duration1);
        const secondDate = DateService.addDuration(compareDate, duration2);
        return DateService.isLessThan(firstDate, secondDate);
    }
    static isZeroDuration(duration) {
        const [key] = Object.keys(duration);
        const typedKey = key;
        return duration[typedKey] === 0;
    }
    static operateOnDate(date, duration, fn) {
        let runningDate = new Date(date.getTime());
        const operate = (value, units) => {
            runningDate = fn(value, units, runningDate);
        };
        for (const key of Object.keys(duration)) {
            switch (key) {
                case 'milliseconds':
                    operate(duration.milliseconds, 'milliseconds');
                    break;
                case 'minutes':
                    operate(duration.minutes, 'minutes');
                    break;
                case 'months':
                    operate(duration.months, 'months');
                    break;
                case 'days':
                    operate(duration.days, 'days');
                    break;
                case 'hours':
                    operate(duration.hours, 'hours');
                    break;
                case 'seconds':
                    operate(duration.seconds, 'seconds');
                    break;
                case 'weeks':
                    operate(duration.weeks, 'weeks');
                    break;
                case 'years':
                    operate(duration.years, 'years');
                    break;
            }
        }
        return runningDate;
    }
    /**
     *
     * @param {*} date1 -- a javascript date
     * @param {*} valueToSubtract -- number value to subtract from date
     * @param {*} units -- moment.js units ex 'days' || 'months'
     *
     * @returns A date
     */
    static subtract(date1, valueToSubtract, units) {
        return moment(date1).subtract(valueToSubtract, units).toDate();
    }
    /**
     * Diff date1 and date2 -- "date1.diff(date2, units)""
     * @param {*} date1
     * @param {*} units
     */
    static hoursSince(date1, units) {
        return moment().diff(date1, units);
    }
    /**
     * Checks if a date1 is less than date2
     *
     * @param {*} date1
     * @param {*} date2
     */
    static isLessThan(date1, date2) {
        if (!date1 || !date2) {
            return undefined;
        }
        if (date1.getTime() < date2.getTime()) {
            return true;
        }
        return false;
    }
    /**
     * Checks if date is less than now
     *
     * @param {*} date
     */
    static isLessThanNow(date) {
        return this.isLessThan(date, new Date());
    }
    static isGreaterThanNow(date) {
        if (!date) {
            return undefined;
        }
        const now = new Date();
        if (date.getTime() > now.getTime()) {
            return true;
        }
        return false;
    }
    static pastNow(valueToAdd, unit) {
        const now = new Date();
        return this.add(now, valueToAdd, unit);
    }
}
exports.DateService = DateService;
// Do not export, consumers should use @engineering11/utility
function isNil(it) {
    return it === undefined || it === null;
}
// time.seconds * 1000 + time.nanoseconds / 1000000,
